/**
 * EVERY DERIVED NUMBER ON THE AUTO-APPROVAL SCREENS, IN ONE FILE.
 *
 * The screens below it render; this file computes. It exists because the
 * design asks for a dozen summary figures — "of 34 scored · 56%", a held
 * breakdown, a shortfall, the rule that settled a decision — and each one is
 * a place a plausible-looking wrong number could be invented. Collected here,
 * each is one short function with a test, and the screens contain no
 * arithmetic at all.
 *
 * THE RULE THIS FILE IS WRITTEN TO: a figure the API cannot support is not
 * rendered. Not estimated, not defaulted to zero, not filled in from a
 * neighbouring field that is nearly the same thing. `features` carries an
 * explicit `null` for every fact the policy could not measure (see
 * `SyncDecisionFeatures` in the API's SyncDecision.interface.m.ts, which
 * spells out why), so absence arrives as data and has to be passed through as
 * absence. Everything here therefore returns `null` rather than `0` when it
 * has nothing, and the screens dash it via `presentation.js`.
 *
 * Nothing here knows about React.
 */

import { isAbsent, prettifyToken } from '../presentation';
import { heldReasonCopy, resolutionBadge } from '../decisionPresentation';

/**
 * THE PARTNER'S NAME, WHICH `partner_name` IS NOT ALWAYS.
 *
 * `SyncDecisionService` sets `partner_name` from the policy table and falls
 * back to `row.PartnerId` when the partner has no entry — so a decision for a
 * partner outside the policy table carries A UUID in the name field. KiwiTaxi
 * is deliberately outside it, and KiwiTaxi is most of the held rows, so
 * rendering `partner_name` straight would put a raw UUID in the Partner column
 * of the busiest screen.
 *
 * Checked against the emitting service rather than assumed from the schema's
 * `default: ''` — the default never applies, because the service always writes
 * something.
 *
 * So: the name if it is a name, otherwise the partner list's name for that id,
 * otherwise the same "(Unknown partner)" the review grid has always shown for
 * an id no partner row matches.
 */
export const partnerNameFor = (row = {}, partners = []) => {
  const given = row.partner_name;
  if (given && given !== row.partner_id) return given;

  const match = (partners || []).find((partner) => partner?.Id === row.partner_id);
  if (match?.name) return match.name;

  return given || '(Unknown partner)';
};

/** Newest `decided_at` first; a row without one sorts last rather than throwing. */
export const byDecidedAtDesc = (a, b) => {
  const left = Date.parse(a?.decided_at ?? '');
  const right = Date.parse(b?.decided_at ?? '');
  if (!Number.isFinite(left) && !Number.isFinite(right)) return 0;
  if (!Number.isFinite(left)) return 1;
  if (!Number.isFinite(right)) return -1;
  return right - left;
};

/**
 * THE NEWEST DECISION WE CAN SEE — which is NOT the same thing as the last
 * run, and the screen says so.
 *
 * The design's header reads "Last run 14:52". There is no run record on the
 * read path: a decision carries `decided_at`, and a pass that looked at
 * nothing writes no row at all. So the honest derivation is the newest
 * `decided_at` in the window, labelled as a decision rather than as a run —
 * otherwise an afternoon in which the daily sweep, a cancellation row and
 * somebody's Run now all started a pass, and none of them found an offer to
 * score, would render as "the pass has not run since 14:52". A pass can run
 * without writing anything, and several things start one.
 *
 * Returns null for an empty window, and the header renders nothing.
 */
export const newestDecisionAt = (rows = []) => {
  const stamps = (Array.isArray(rows) ? rows : [])
    .map((row) => row?.decided_at)
    .filter((at) => Number.isFinite(Date.parse(at ?? '')));

  if (stamps.length === 0) return null;

  return stamps.reduce((newest, at) => (Date.parse(at) > Date.parse(newest) ? at : newest));
};

const countOf = (counts, key) => {
  const value = Number(counts?.[key]);
  return Number.isFinite(value) ? value : 0;
};

/**
 * The first counter card: how many the policy approved on its own, out of how
 * many it scored.
 *
 * "Scored" is the sum of every verdict in `by_decision` MINUS `not_evaluated`,
 * because a `not_evaluated` row is one the policy never saw (a person decided
 * directly) and counting it in the denominator would make the policy look
 * worse the more work people did by hand. See `SYNC_DECISIONS` in the API
 * interface, which spells out that `not_evaluated` carries no verdict.
 *
 * `share` is null rather than 0 when nothing was scored: "0%" of nothing is a
 * claim about the policy, and an empty window is not one.
 */
export const scoredTotals = (byDecision = {}) => {
  const approved = countOf(byDecision, 'auto_approved');
  const held = countOf(byDecision, 'held');
  const rejected = countOf(byDecision, 'auto_rejected');
  const notEvaluated = countOf(byDecision, 'not_evaluated');
  const scored = approved + held + rejected;

  return {
    approved,
    held,
    rejected,
    notEvaluated,
    scored,
    share: scored > 0 ? approved / scored : null,
  };
};

/**
 * The held breakdown line — "4 margin below floor · 9 demand unknown".
 *
 * The LABELS COME FROM THE ENUM KEYS, through `prettifyToken`, and the full
 * sentence comes from `heldReasonCopy`. Neither is invented here. The design
 * writes them shorter still ("no price" for `no_quote`), but a third spelling
 * of a closed vocabulary is a third thing to reconcile against the API, and
 * this one was reconciled last week — so the key's own words are used and the
 * reconciled sentence rides along as the tooltip.
 *
 * Biggest first: the breakdown is read to decide what to fix.
 */
export const heldBreakdown = (byHeldReason = {}) => Object.keys(byHeldReason || {})
  .map((reason) => ({
    reason,
    count: countOf(byHeldReason, reason),
    label: prettifyToken(reason).toLowerCase(),
    copy: heldReasonCopy(reason),
  }))
  .filter((entry) => entry.count > 0)
  .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));

/**
 * THE RULES THAT WERE RECORDED AND COULD NOT REFUSE.
 *
 * `advisory: true` is written only on a rule the partner's policy stood down
 * — today the commercial rules (`margin_floor`, `demand_certain`) for a
 * partner whose prices we published. The rule still ran and its measurement
 * is still on the record; what it lost is the power to hold the offer.
 *
 * Absence claims NOTHING, which is why this tests for `true` and not for
 * falsiness: every record written before the field existed, and every rule
 * that did gate, carries no `advisory` at all.
 */
export const advisoryRules = (row = {}) => (Array.isArray(row.rules_fired) ? row.rules_fired : [])
  .filter((rule) => rule?.advisory === true);

/**
 * THE RULE THAT SETTLED IT — which an ADVISORY RULE NEVER IS.
 *
 * `rules_fired` stops at the first refusal — the API interface says so
 * explicitly ("Evaluation stops at the first refusal") — so a failing rule is
 * the one that decided a hold, and when every rule that COULD refuse passed,
 * the last of those is the one that cleared it.
 *
 * Advisory rules are dropped before either question is asked. A rule that was
 * not allowed to refuse cannot have held an offer and cannot be the thing
 * that cleared one, so naming it would credit the decision to a rule that
 * had no part in it — and an advisory rule is evaluated LAST on the arm it
 * applies to, which is exactly where "the last one evaluated" would have
 * picked it up. Dropped before the refusal search too, not after, so a row
 * that breaks the contract by carrying `advisory: true` with `pass: false`
 * still cannot be named as the decider.
 *
 * Returns null for a row with no gating rule in its trace, rather than naming
 * a rule that did not run or one that could not decide.
 */
export const settlingRule = (row = {}) => {
  const gating = (Array.isArray(row.rules_fired) ? row.rules_fired : [])
    .filter((rule) => rule?.advisory !== true);
  if (gating.length === 0) return null;

  const failed = gating.find((rule) => rule?.pass === false);
  if (failed) return { id: failed.id, failed: true, rule: failed };

  const last = gating[gating.length - 1];
  return { id: last?.id, failed: false, rule: last };
};

/**
 * What accepting a wrong hold would have cost: offer − our price.
 *
 * Null unless BOTH numbers are real. A shortfall computed against a missing
 * quote is the offer itself wearing a minus sign, which on a screen somebody
 * accepts transfers from reads as a measured loss.
 */
export const shortfallOf = (features = {}) => {
  const ours = features?.our_price;
  const theirs = features?.offer;
  if (isAbsent(ours) || isAbsent(theirs)) return null;
  const left = Number(theirs);
  const right = Number(ours);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return null;
  return left - right;
};

/** The post step with this id, or null. Ids are matched loosely — see below. */
const stepLike = (row, pattern) => (Array.isArray(row?.post_steps) ? row.post_steps : [])
  .find((step) => pattern.test(String(step?.id || '')));

/**
 * WHAT BECAME OF THE OFFER, in one line for a table cell.
 *
 * Composed from the badges and copy that already exist rather than restating
 * them: the resolution's own label, then the post-approval facts that a
 * reader of the log actually asks about — did the partner hear about it, and
 * did a booking get written. A disagreement is named, because an approval
 * against a hold is the row the log exists for.
 *
 * Steps are matched on the id CONTAINING the word, not on an exact value, so
 * a backend rename does not silently drop the line (the same reasoning as
 * `isPartnerAcceptStep` in decisionPresentation.js).
 */
export const outcomeLine = (row = {}) => {
  if (row.decision === 'not_evaluated') {
    return 'Decided by a person — no policy ran';
  }

  if (row.resolution == null) {
    return row.decision === 'held'
      ? 'Held — waiting for a person'
      : `${resolutionBadge(null).label} — nothing has acted on it`;
  }

  const parts = [resolutionBadge(row.resolution).label];

  if (row.overridden_by) parts.push('by hand, against the policy');

  const accept = stepLike(row, /accept/i);
  if (accept) parts.push(accept.ok === false ? 'partner accept FAILED' : 'accepted on the partner');

  const write = stepLike(row, /booking|write/i);
  if (write) parts.push(write.ok === false ? 'booking write FAILED' : 'booked');

  return parts.join(' · ');
};

/**
 * WHOSE PRICES THESE OFFERS WERE, as the decisions in the window show it.
 *
 * `unknown` and `partner_priced` are deliberately separate values and not one
 * "no rate sheet": the first is "we did not observe the basis", the second is
 * "we observed that they quote us". Only the second licences a sentence about
 * a floor applying, and only the first is a gap in what we read.
 */
export const POLICY_BASIS = {
  RATE_SHEET: 'rate_sheet',
  PARTNER_PRICED: 'partner_priced',
  CHANGED: 'changed',
  UNKNOWN: 'unknown',
};

/**
 * THE POLICY AS THE DECISIONS SHOW IT, not as a settings endpoint states it.
 *
 * There is no read endpoint for the auto-approval policy — no mode, no floor,
 * no ceiling. What there IS, on every decision, is the flag as it stood when
 * that decision was taken (`auto_approval_enabled`, stored per record exactly
 * so a later flag flip cannot rewrite history) and the thresholds the rules
 * were measured against (`rules_fired[].threshold`, stored so "a threshold
 * change is readable"). Both are the real policy, observed.
 *
 * So this returns OBSERVATIONS and the panel labels them as observations. It
 * never fills a gap: a partner whose rows carry no `margin_floor` threshold
 * gets `floor: null` and a dash, not an assumed −5%.
 *
 * `mode` has a third value on purpose. A window in which the flag was on for
 * some rows and off for others is not "on" and not "off" — it is a window in
 * which somebody changed it, and that is worth seeing rather than averaging
 * away.
 *
 * `basis` IS THE SECOND OBSERVED FACT, and it is read the same way for the
 * same reason. `rate_sheet` on a decision records WHOSE PRICES the offer was
 * when it was taken: `true` means we published the rate sheet, so the
 * commercial rules were measured and stood down; `false` means the partner
 * quoted us and those rules gated in full. It is kept as a PAIR of sightings
 * rather than one value, exactly as the flag is, so a window in which a
 * partner moved between the two arms reads as changed instead of having one
 * arm quietly win.
 *
 * ABSENCE IS NOT `false` HERE and the pair is what keeps that true. The field
 * is written only when a reviewed policy entry exists, so a decision for a
 * partner nobody has reviewed carries no `rate_sheet` at all — and every
 * record written before the field existed carries none either. Such a row
 * sets NEITHER sighting, which leaves `basis: 'unknown'`: we did not observe
 * the basis, which is a different statement from observing that the partner
 * prices us. Reading absence as `false` would stamp "they quote us" — a
 * reviewed commercial claim — onto partners nobody has reviewed.
 */
export const observedPolicy = (rows = [], partners = []) => {
  const byPartner = new Map();

  (Array.isArray(rows) ? rows : []).slice().sort(byDecidedAtDesc).forEach((row) => {
    if (!row) return;
    const key = row.partner_id || row.partner_name || '(unidentified partner)';

    if (!byPartner.has(key)) {
      byPartner.set(key, {
        partnerId: row.partner_id || null,
        partnerName: partnerNameFor(row, partners),
        decisions: 0,
        enabledSeen: false,
        disabledSeen: false,
        rateSheetSeen: false,
        partnerPricedSeen: false,
        floor: null,
        ceiling: null,
        acceptRate: null,
      });
    }

    const entry = byPartner.get(key);
    entry.decisions += 1;

    // A CANCELLATION IS NOT A FLAG SIGHTING. The cancellation handler stamps
    // `auto_approval_enabled` onto its record like every other writer, but the
    // flag plays no part in what it decided — it refuses inside the notice
    // window whatever the flag says. Counting it as a sighting made a
    // flag-OFF cancellation beside a flag-ON offer read as
    // `mode: 'changed'` — "changed in this window" — which asserts somebody
    // moved the flag when nobody did. Partner Sync's whole claim is that it
    // reports what it OBSERVED, so an observation it cannot stand behind is
    // worse here than a missing one.
    if (row.decision !== 'partner_cancelled') {
      if (row.auto_approval_enabled === true) entry.enabledSeen = true;
      if (row.auto_approval_enabled === false) entry.disabledSeen = true;
    }
    // Strict, both ways: an absent `rate_sheet` is neither sighting.
    if (row.rate_sheet === true) entry.rateSheetSeen = true;
    if (row.rate_sheet === false) entry.partnerPricedSeen = true;

    // Rows arrive newest-first, so the first threshold seen is the newest one
    // in force and later (older) rows must not overwrite it.
    const rules = Array.isArray(row.rules_fired) ? row.rules_fired : [];
    rules.forEach((rule) => {
      if (isAbsent(rule?.threshold)) return;
      if (entry.floor === null && /margin|floor/i.test(String(rule.id || ''))) {
        entry.floor = Number(rule.threshold);
      }
      if (entry.ceiling === null && /ceiling|cap/i.test(String(rule.id || ''))) {
        entry.ceiling = Number(rule.threshold);
      }
    });

    if (entry.acceptRate === null && !isAbsent(row.features?.partner_accept_rate)) {
      entry.acceptRate = Number(row.features.partner_accept_rate);
    }
  });

  return [...byPartner.values()]
    .map((entry) => {
      let basis = POLICY_BASIS.UNKNOWN;
      if (entry.rateSheetSeen && entry.partnerPricedSeen) basis = POLICY_BASIS.CHANGED;
      else if (entry.rateSheetSeen) basis = POLICY_BASIS.RATE_SHEET;
      else if (entry.partnerPricedSeen) basis = POLICY_BASIS.PARTNER_PRICED;

      let mode = 'unknown';
      if (entry.enabledSeen && entry.disabledSeen) mode = 'changed';
      // THE ONE COMBINATION THAT NEEDED A PILL OF ITS OWN: switched on, and
      // the margin cannot hold an offer. "auto-approval on" alone invites the
      // reader to assume a floor was cleared, which for a rate-sheet partner
      // is not what happened — the floor was never applied. The flag's other
      // states do not split: with the flag off nothing was approved whoever
      // set the price, and a window in which the flag itself changed is that
      // before it is anything else.
      else if (entry.enabledSeen) {
        mode = basis === POLICY_BASIS.RATE_SHEET ? 'on_rate_sheet' : 'on';
      } else if (entry.disabledSeen) mode = 'off';

      return { ...entry, basis, mode };
    })
    .sort((a, b) => b.decisions - a.decisions
      || String(a.partnerName || '').localeCompare(String(b.partnerName || '')));
};

/** The mode pill's words and tone. Grey for "we cannot tell from these rows". */
export const POLICY_MODE_PILLS = {
  on: { label: 'auto-approval on', bg: 'success' },
  // Still green — it IS on — but it never lets "on" stand alone, because the
  // margin rules on this arm are recorded and cannot refuse.
  on_rate_sheet: { label: 'auto-approval on · margin does not gate', bg: 'success' },
  off: { label: 'auto-approval off', bg: 'secondary' },
  changed: { label: 'changed in this window', bg: 'warning', text: 'dark' },
  unknown: { label: 'not recorded', bg: 'light', text: 'dark' },
};
